import React, { Component, ChangeEvent } from 'react';
import { Redirect } from 'react-router-dom';
import InputWithLabel, { SelectOption } from '../common/InputWithLabel';
import LoadingButtonBase from '../common/LoadingButtonBase';
import TaxpayerPortalPanel from '../common/TaxpayerPortalPanel';
import { Alert, Row, Col, Container } from 'reactstrap';
import { Location } from 'history';
import LocationInput from './LocationInput';
import { filterMoneyInput, addZeroes } from '../../utility/InputFunctions';
import { parseCurrency } from '../../utility/CurrencyFormatter';
import { ReturnEntryState, GrossReceiptReturnTypes, BillDetails, GrossReceiptReturn } from '../../types/GrossReceiptReturnTypes';
import { isFoodBeverage, isRoomOccupancy, mustBeFoodBeverage, isHeavyEquipment, isSplitValueVehicle } from '../../utility/TypeGuards';
import { History } from 'history';
import { verifyIsRoomOccupancy, VerifyHistoryState, RoomOccupancyVerifyHistoryState, verifyIsFoodBeverage, verifyIsSplitValueVehicle } from '../VerifyReturn';
import { post, mockSuccessResponse, parseResponse } from '../../utility/FetchFunctions';
import { FetchSuccess, FetchError } from '../../types/FetchTypes';
import { ReturnLocation, FoodBeverageReturnLocation, SplitVehicleLocation } from '../../types/LocationTypes';
import moment from 'moment';
import FloatingDismissibleAlert from '../common/FloatingDismissableAlert';
import { SplitValueVehiclePrintHistoryState } from '../PrintableReturnInfo';
import { grossReceiptTypes } from '../../utility/Constants';


type ReturnEntryProps = {
    location: Location<GrossReceiptReturn>;
    history: History;
    isTest: boolean;
};

type DefaultCurrencyStatesUpdateableOnChange = keyof Pick<GrossReceiptReturnTypes, 'excess' | 'creditRefunded' | 'creditApplied'>;

export default class ReturnEntry extends Component<ReturnEntryProps, ReturnEntryState> {
    constructor(props: Readonly<ReturnEntryProps>) {
        super(props);
        const incomingState = this.props.location.state;
        const grossReceiptReturn: GrossReceiptReturnTypes = {
            accountNum: incomingState.accountNum ?? '',
            pin: incomingState.pin ?? '',
            excess: this.formatReturnValue(incomingState.excess),
            dueDate: incomingState.dueDate ?? '',
            grossReceiptType: incomingState.grossReceiptType ?? '',
            ownerName: incomingState.ownerName ?? '',
            ownerAddress: incomingState.ownerAddress ?? '',
            locations: incomingState.locations ?? [],
            grossReceiptTypeCode: incomingState.grossReceiptTypeCode,
            creditRemaining: incomingState.creditRemaining ?? '',
            creditRefunded: this.formatReturnValue(incomingState.creditRefunded),
            creditApplied: this.formatReturnValue(incomingState.creditApplied),
            returnMonth: incomingState.returnMonth,
            returnYear: incomingState.returnYear,
            returnFiscalYear: incomingState.returnFiscalYear,
            filedBy: incomingState.filedBy,
            splitVehicleOverByYear: incomingState.splitVehicleOverByYear,
            splitVehicleUnderByYear: incomingState.splitVehicleUnderByYear
        };
        this.state = {
            grossReceiptReturn: grossReceiptReturn,
            error: '',
            loading: true,
            countyPhoneNumber: '',
            informationalMessages: []
        };
    }

    componentDidMount = () => {
        this.getCountyPhoneNumber().then(() => this.setState({
            loading: false
        }));
    }

    getCountyPhoneNumber = async () => {
        if (this.props.isTest) {
            this.setState({
                countyPhoneNumber: 'N/A'
            });
            return;
        } 

        try {
            const response = parseResponse<string, string>(await fetch('api/configuration/CountyPhoneNumber'));
            if (response.ok) {
                const phoneNum = await response.getValue?.();
                this.setState({
                    countyPhoneNumber: phoneNum ?? 'N/A',

                });
                return;
            }
        }
        catch (error) {
            console.log(error);
            return false;
        }
    }

    public static defaultProps = {
        isTest: false,
    };

    static displayName = ReturnEntry.name;

    // Wrap the call to post to allow us to prevent making fetch calls when testing the component
    numericPost(route: string, postBodyData: any): Promise<FetchSuccess<number | null> | FetchError> {
        return this.props.isTest ? mockSuccessResponse<number>(0) : post<number>(route, postBodyData);
    }

    objectPost(route: string, postBodyData: any): Promise<FetchSuccess<BillDetails | null> | FetchError> {
        return this.props.isTest ? mockSuccessResponse<BillDetails>({ totalTaxAmount: 1, totalPenalties: 1, totalBillAmount: 1, billDisplay: "test", isReturnFiled: false }) : post<BillDetails>(route, postBodyData);
    }

    formatReturnValue(input: string | undefined): string {
        let formatted = input ?? '';
        return formatted === '0' ? '' : formatted;
    }

    updateGrossReceiptsForLocation = (index: number, value: string) => {

        this.setState((oldState) => {
            return {
                grossReceiptReturn: {
                    ...oldState.grossReceiptReturn,
                    locations: [
                        ...oldState.grossReceiptReturn.locations.slice(0, index),
                        {
                            ...oldState.grossReceiptReturn.locations[index],
                            grossReceipts: value,
                        },
                        ...oldState.grossReceiptReturn.locations.slice(index + 1),
                    ]
                },
            };
        });

    }

    updateSplitVehicleReceiptsForLocation = (index: number, value: string | undefined, isOver: boolean, otherValue?: string) => {
        const parsedValue = Number(value);
        const parsedOtherValue = Number(otherValue ?? 0);
        this.setState((oldState) => {
            return {
                grossReceiptReturn: {
                    ...oldState.grossReceiptReturn,
                    locations: [
                        ...oldState.grossReceiptReturn.locations.slice(0, index),
                        {
                            ...oldState.grossReceiptReturn.locations[index],
                            [isOver ? "grossReceiptsOver" : "grossReceiptsUnder"]: value,
                            grossReceipts: String(parsedValue + parsedOtherValue),
                        },
                        ...oldState.grossReceiptReturn.locations.slice(index + 1),
                    ]
                },
            };
        });
    }

    updateRoomOccupancyReceiptsForLocation = (index: number, field: string, value: string) => {
        this.setState((oldState) => {
            return {
                grossReceiptReturn: {
                    ...oldState.grossReceiptReturn,
                    locations: [
                        ...oldState.grossReceiptReturn.locations.slice(0, index),
                        {
                            ...oldState.grossReceiptReturn.locations[index],                            
                            [field]: value
                        },
                        ...oldState.grossReceiptReturn.locations.slice(index + 1),
                    ]
                },
            };
        });
    }

    updateNonPreparedReceiptsForLocation = (index: number, value: string) => {
        this.setState((oldState) => {
            mustBeFoodBeverage(oldState.grossReceiptReturn);
            return {
                grossReceiptReturn: {
                    ...oldState.grossReceiptReturn,
                    locations: [
                        ...oldState.grossReceiptReturn.locations.slice(0, index),
                        {
                            ...oldState.grossReceiptReturn.locations[index],
                            nonPreparedFoodBeverageReceipts: value,
                        },
                        ...oldState.grossReceiptReturn.locations.slice(index + 1),
                    ]
                },
            };
        });
    }

    updateCurrency = (event: ChangeEvent<HTMLInputElement>, allowedDigitsBeforeDecimal: number, stateProp: DefaultCurrencyStatesUpdateableOnChange, blur: boolean = false): void =>
        this.updateDecimalFields(event, allowedDigitsBeforeDecimal, (newValue) => this.setState((oldState) => ({
            ...oldState,
            grossReceiptReturn: {
                ...oldState.grossReceiptReturn,
                [stateProp]: newValue,
            },
        })), blur);

    updateDecimalFields = (event: ChangeEvent<HTMLInputElement>, allowedDigitsBeforeDecimal: number, updateState: (newValue: string) => void, blur: boolean) => {
        const newValue = blur ? addZeroes(event.target.value) : filterMoneyInput(event, allowedDigitsBeforeDecimal)
        if (newValue !== undefined) {
            updateState(newValue);
        }
    }

    validateReturnFields = () => {
        let errorMessage = "";
        let valid = true;
        const grossReceiptReturn = this.state.grossReceiptReturn;

        if (!grossReceiptReturn.filedBy) {
            valid = false;
            errorMessage += "Filed by is required. ";
        }

        if (grossReceiptReturn.dueDate === '') {
            valid = false;
            errorMessage += "Due date is required. ";
        }

        if (isFoodBeverage(grossReceiptReturn)) {
            if (grossReceiptReturn.locations.find(loc => Number(loc.nonPreparedFoodBeverageReceipts) && !Number(loc.grossReceipts))) {
                valid = false;
                errorMessage += "Non-prepared value cannot exist without Gross Receipt value. "
            }
            if (grossReceiptReturn.locations.find(loc => loc.nonPreparedFoodBeverageReceipts && loc.grossReceipts && Number(loc.nonPreparedFoodBeverageReceipts) > Number(loc.grossReceipts))) {
                valid = false;
                errorMessage += "Non-prepared Food/Beverage value cannot be greater than Total Gross Receipt value. "
            }
        }

        if (isRoomOccupancy(grossReceiptReturn)) {
            const errors: string[] = [];
            let skipSumCheck: boolean = false;

            if (grossReceiptReturn.locations.find(loc => Number(loc.extendedStays ?? 0) > Number(loc.grossReceipts ?? 0))) {
                errors.push("Extended Stays Value should not exceed Gross Retail Receipts value");
                skipSumCheck = true;
            }

            if (grossReceiptReturn.locations.find(loc => Number(loc.governmentExemptions ?? 0) > Number(loc.grossReceipts ?? 0))) {
                errors.push("Government Exemption Value should not exceed Gross Retail Receipts value");
                skipSumCheck = true;
            }

            if (grossReceiptReturn.locations.find(loc => Number(loc.nonOccupancy ?? 0) > Number(loc.grossReceipts ?? 0))) {
                errors.push("Non-Occupancy Value should not exceed Gross Retail Receipts value");
                skipSumCheck = true;
            }

            if (!skipSumCheck && grossReceiptReturn.locations.find(loc => Number(loc.grossReceipts ?? 0) - Number(loc.extendedStays ?? 0) - Number(loc.governmentExemptions ?? 0) - Number(loc.nonOccupancy ?? 0) < 0)) {
                errors.push("The sum of Extended Stays, Government Exemption, and Non-Occupancy values should not exceed Gross Receipts value");
            }

            if (errors.length > 0) {
                valid = false;
                errorMessage += `${errors.join('. ')}.`;
            }           
        }

        if (Number((Number(grossReceiptReturn.creditRemaining) + Number(grossReceiptReturn.creditRefunded)).toFixed(2)) < Number(grossReceiptReturn.creditApplied)) {
            valid = false;
            errorMessage += "Credit applied cannot be greater than the credit remaining combined with credit refunded.";
        }

        this.setState({
            error: errorMessage ? `Submission failed: ${errorMessage}` : ""
        });
        return valid;
    }

    get getDueDate() {
        const dueDate = this.state.grossReceiptReturn.dueDate;
        //dueDate should be at the beginning of the day
        return dueDate ? moment.utc(dueDate, "YYYY-MM-DD").startOf('day').toDate() : moment().utc().startOf('day').toDate()
    }

    public get printType(): VerifyHistoryState | RoomOccupancyVerifyHistoryState | SplitValueVehiclePrintHistoryState {
        const grossReceiptReturn = this.state.grossReceiptReturn;
        const returnValue: VerifyHistoryState = {
            accountNum: Number(grossReceiptReturn.accountNum),
            pin: grossReceiptReturn.pin,
            excessTaxAmount: Number(this.state.grossReceiptReturn.excess),
            dueDate: this.getDueDate,
            ownerName: grossReceiptReturn.ownerName,
            locations: grossReceiptReturn.locations,
            ownerAddressLine1: grossReceiptReturn.ownerAddress,
            totalSalesValue: (this.state.grossReceiptReturn as GrossReceiptReturn).locations.reduce((x, y) => x + Number(y.grossReceipts ?? 0), 0),
            returnTypeDescription: grossReceiptReturn.grossReceiptType,
            returnTypeCode: grossReceiptReturn.grossReceiptTypeCode,
            creditRemaining: grossReceiptReturn.creditRemaining,
            creditRefunded: Number(grossReceiptReturn.creditRefunded),
            creditApplied: Number(grossReceiptReturn.creditApplied),
            loading: false,
            returnMonth: grossReceiptReturn.returnMonth as number,
            returnYear: grossReceiptReturn.returnYear as number,
            returnFiscalYear: grossReceiptReturn.returnFiscalYear as number,
            filedBy: grossReceiptReturn.filedBy,
            splitVehicleUnderByYear: grossReceiptReturn.splitVehicleUnderByYear,
            splitVehicleOverByYear: grossReceiptReturn.splitVehicleOverByYear,
            error: '',
            countyPhoneNumber: this.state.countyPhoneNumber
        }
        if (isRoomOccupancy(grossReceiptReturn) && verifyIsRoomOccupancy(returnValue)) {
            returnValue.totalSalesValue = (returnValue.totalSalesValue ?? 0)
                - (grossReceiptReturn.locations.map(v => Number(v.extendedStays ?? 0) + Number(v.governmentExemptions ?? 0) + Number(v.nonOccupancy ?? 0)).reduce((x, y) => x + y) ?? 0);
        }
        if (isFoodBeverage(grossReceiptReturn) && verifyIsFoodBeverage(returnValue)) {
            returnValue.totalSalesValue = (returnValue.totalSalesValue ?? 0) - (grossReceiptReturn.locations.map(v => Number(v.nonPreparedFoodBeverageReceipts ?? 0)).reduce((x, y) => x + y) ?? 0);
        }
        if (isSplitValueVehicle(grossReceiptReturn, grossReceiptReturn.returnFiscalYear) && verifyIsSplitValueVehicle(returnValue, grossReceiptReturn.returnFiscalYear)) { }
        return returnValue;
    }

    handleDateUpdate = (event: ChangeEvent<HTMLInputElement>) => this.setState({
        grossReceiptReturn: {
            ...this.state.grossReceiptReturn,
            dueDate: event.target.value,
        },
        error: ""
    });

    getSplitUnderValue = (year: number | null) => year ? this.state.grossReceiptReturn.splitVehicleUnderByYear[year] ?? undefined : undefined;
    getSplitOverValue = (year: number | null) => year ? this.state.grossReceiptReturn.splitVehicleOverByYear[year] ?? undefined : undefined;

    addInfoMessage = (messageToAdd: string) => {
        if (!this.state.informationalMessages.includes(messageToAdd)) {
            this.setState((prevState) => ({
                informationalMessages: [...prevState.informationalMessages, messageToAdd],
            }));
        }
    }
    removeInfoMessage = (messageToRemove: string) => {
        this.setState((oldState) => {
            const updatedMessages = oldState.informationalMessages.filter(message => message !== messageToRemove);
            return {
                informationalMessages: updatedMessages
            };
        });
    }

    updateSplitVehicle = (newFiscalYear: number) => {
        let updatedLocations = this.state.grossReceiptReturn.locations;
        let oldSplitUnder = this.getSplitUnderValue(this.state.grossReceiptReturn.returnFiscalYear);
        let newSplitUnder = this.getSplitUnderValue(newFiscalYear)
        let oldSplitOver = this.getSplitOverValue(this.state.grossReceiptReturn.returnFiscalYear);
        let newSplitOver = this.getSplitOverValue(newFiscalYear);

        const splitChangeMessage = "Split value vehicle information has changed. Please review and update the gross receipts for each location.";
        if ((oldSplitUnder !== newSplitUnder || oldSplitOver !== newSplitOver)) {
            if (updatedLocations.some(loc => Number((loc as SplitVehicleLocation).grossReceiptsUnder ?? 0) > 0 || Number((loc as SplitVehicleLocation).grossReceiptsOver ?? 0) > 0 || Number(loc.grossReceipts ?? 0) > 0)) {
                this.addInfoMessage(splitChangeMessage);
            }
            else {
                this.removeInfoMessage(splitChangeMessage);
            }
            updatedLocations.forEach((loc, i) => {
                (loc as SplitVehicleLocation).grossReceiptsUnder = undefined;
                (loc as SplitVehicleLocation).grossReceiptsOver = undefined;
                loc.grossReceipts = undefined;
            });
        }
        else {
            this.removeInfoMessage(splitChangeMessage);
        }

        return updatedLocations;
    }

    handleReturnYear = (event: ChangeEvent<HTMLInputElement>) => {
        let updatedLocations = this.state.grossReceiptReturn.locations;
        const newYear = Number(event.target.value);
        const newFiscalYear = this.state.grossReceiptReturn.returnMonth == undefined || this.state.grossReceiptReturn.returnMonth == null || this.state.grossReceiptReturn.returnMonth < 7 ? newYear - 1 : newYear;

        if (this.state.grossReceiptReturn.grossReceiptTypeCode === grossReceiptTypes.vehicle && newFiscalYear !== null) {
            updatedLocations = this.updateSplitVehicle(newFiscalYear);
        }

        this.setState({
            grossReceiptReturn: {
                ...this.state.grossReceiptReturn,
                returnYear: newYear,
                returnFiscalYear: newFiscalYear,
                locations: updatedLocations
            }
        }, () => {
            if (this.state.grossReceiptReturn.returnYear === new Date().getFullYear() &&
                this.state.grossReceiptReturn.returnMonth !== null &&
                this.state.grossReceiptReturn.returnMonth > new Date().getMonth() + 1) {
                this.setState({
                    grossReceiptReturn: {
                        ...this.state.grossReceiptReturn,
                        returnMonth: null,
                        dueDate: ''
                    }
                });
            } else {
                this.calculateBillDueDate();
            }            
        });
    }

    handleReturnMonth = (event: ChangeEvent<HTMLInputElement>) => {
        let updatedLocations = this.state.grossReceiptReturn.locations;
        const newMonth = Number(event.target.value);
        const newFiscalYear = this.state.grossReceiptReturn.returnYear != undefined && this.state.grossReceiptReturn.returnYear != null && newMonth < 7 ? this.state.grossReceiptReturn.returnYear - 1 : this.state.grossReceiptReturn.returnYear;

        if (this.state.grossReceiptReturn.grossReceiptTypeCode === grossReceiptTypes.vehicle && newFiscalYear !== null) {
            updatedLocations = this.updateSplitVehicle(newFiscalYear);
        }

        this.setState({
            grossReceiptReturn: {
                ...this.state.grossReceiptReturn,
               returnMonth: newMonth,
               returnFiscalYear: newFiscalYear,
               locations: updatedLocations
            }
        }, this.calculateBillDueDate)
    }

    isValidReturnYear = (year: number | null, maxYear: number) => {
        if (year === undefined || year === null || year < 1753 || year > maxYear) {
            return false;
        }

        return true;
    }

    fetchDefaultBillDueDate = async (): Promise<FetchSuccess<string | null> | FetchError> => {
        const response = await fetch(`api/GrossReceiptReturn/GetGrossReceiptDefaultBillDueDate?grossReceiptType=${this.state.grossReceiptReturn.grossReceiptTypeCode}&returnMonth=${this.state.grossReceiptReturn.returnMonth}&returnYear=${this.state.grossReceiptReturn.returnYear}`);
        return parseResponse(response);
    }

    getContactCountyErrorPart = () => `Please contact the tax office at ${this.state.countyPhoneNumber}.`;

    calculateBillDueDate = async () => {
        let dueDate: string = '';
        let error: string = '';
        const returnMonth = this.state.grossReceiptReturn.returnMonth;
        const returnYear = this.state.grossReceiptReturn.returnYear;

        if (returnMonth && this.isValidReturnYear(returnYear, new Date().getFullYear())) {
            const result = await this.fetchDefaultBillDueDate();
            if (result.ok && result.getValue) {
                dueDate = (await result?.getValue()) ?? '';
            } else {
                error = `An error occurred while calculating the bill due date. ${this.getContactCountyErrorPart()}`;
            }
        }

        this.setState({
            grossReceiptReturn: {
                ...this.state.grossReceiptReturn,
                dueDate: dueDate,           
            },
            error: error
        });
    }

    readonly submitReturn = async () => {
        const verifyValue = this.printType;
        this.setState({ informationalMessages: [] });
        if (this.validateReturnFields()) {
            let error = "Error while submitting";
            let validateCreditApplied, validateReturnFiled = false;
            this.setState({ loading: true })
            try {
                const response = await this.objectPost('api/GrossReceiptReturn/GetGrossReceiptBillAmount', verifyValue)
                if (response.ok) {
                    const result = await response.getValue?.();
                    if (result !== null && result !== undefined) {
                        verifyValue.currentDue = Number(result.totalBillAmount);
                        verifyValue.penaltiesTotal = Number(result.totalPenalties);
                        verifyValue.taxTotal = Number(result.totalTaxAmount);
                        verifyValue.billDisplay = result.billDisplay ?? "";
                        if (result.isReturnFiled) {
                            validateReturnFiled = true;
                        }
                        //credit applied is greater than total tax
                        else if (Number(this.state.grossReceiptReturn.creditApplied) > (verifyValue.taxTotal)) {
                            validateCreditApplied = true;
                        }
                        else {
                            this.props.history.push('/enter-return/verify', verifyValue);
                            return;
                        }
                    }
                }

                if (validateCreditApplied) {
                    error += `: credit applied cannot be greater than total tax - ${parseCurrency(verifyValue.taxTotal)}`
                }
                else if (validateReturnFiled) {
                    error += `: There is already a return filed with Bill Due Date ${moment(this.state.grossReceiptReturn.dueDate, "YYYY-MM-DD").format("MM/DD/YYYY")}.`
                }
                else {
                    error += `: unable to determine amount due. ${this.getContactCountyErrorPart()}`
                }

            } catch {
                error += `: the request could not be sent. ${this.getContactCountyErrorPart()}`
            }

            this.setState({
                loading: false,
                error: error,
            });
        }
    }

    get error() {
        return this.state.error
            ? <FloatingDismissibleAlert color="danger" clearError={() => this.setState({ error: '' })}>
                {this.state.error}
            </FloatingDismissibleAlert>
            : <></>;
    };

    get floatingInfo() {
        const informationalMessages = this.state.informationalMessages.join("<br/>");

        return informationalMessages ? (
            <FloatingDismissibleAlert color="warning" clearError={() => this.setState({informationalMessages: []}) }>
                {informationalMessages}
            </FloatingDismissibleAlert>
        ) : (
            <></>
        );
    }

    get info() {
        let creditApplied = this.state.grossReceiptReturn.creditApplied;
        return Number(creditApplied) > 0 ?
            (
                <Alert color="warning">
                    You can not pay online when applying credit, please mail in payment.
                </Alert>
            ) :
            (<></>);
    };

    get accountInformation() {
        return (
            <TaxpayerPortalPanel header="Account Information" className="bordered-panel borderless-bottom">
                <div className="panel-item">Account Number: {this.state.grossReceiptReturn.accountNum}</div>
                <div className="panel-item">Owner Name: {this.state.grossReceiptReturn.ownerName}</div>
                <div className="panel-item">Owner Address: {this.state.grossReceiptReturn.ownerAddress}</div>
                <div className="panel-item">Account Type: {this.state.grossReceiptReturn.grossReceiptType}</div>
                <div className="panel-item">Credit Remaining: {parseCurrency(this.state.grossReceiptReturn.creditRemaining)}</div>
            </TaxpayerPortalPanel>
        );
    }

    getFormattedRow = (columnList: React.ReactNode[], key: number) => {
        const first: React.ReactNode = columnList[0];
        const second = columnList.length > 1 ? columnList[1] : <></>;
        return (
            <Row key={key}>
                <Col className="padded-col">
                    {first}
                </Col>
                {(first && React.isValidElement(first) && first.props.fullWidth !== true) && <Col className="padded-col">
                    {second}
                </Col>}
            </Row>
        );
    }

    getFormattedColumns = (columnList: React.ReactNode[]) => {
        let chunked: React.ReactNode[][] = [];
        let i = 0;
        while (i < columnList.length) {
            const curElement: React.ReactNode = columnList[i]
            const nextElement: React.ReactNode | null = i+1 < columnList.length ? columnList[i+1] : null;

            if ((React.isValidElement(curElement) && curElement.props && curElement.props.fullWidth) || 
                (React.isValidElement(nextElement) && nextElement.props && (nextElement.props as any).fullWidth)
            ) {
                if (React.isValidElement(curElement) && curElement.props && curElement.props.fullWidth) {
                    chunked.push([curElement]);
                    i ++;
                } else if (React.isValidElement(nextElement) && nextElement.props && (nextElement.props as any).fullWidth) {
                    chunked.push([curElement]);
                    chunked.push([nextElement]);
                    i += 2;
                }
            } else {
                chunked.push(columnList.slice(i, i + 2));
                i += 2;
            }
        }
        
        return (
            <Container>
                {chunked.map((com, i) => this.getFormattedRow(com, i))}
            </Container>
        );
    }

    buildPeriodDropDown = (): React.ReactNode => {
        let options: SelectOption[] = [];
        let label: string = '';
        if (isHeavyEquipment(this.state.grossReceiptReturn)) {
            options = [
                { value: '', label: '' },
                { value: 3, label: 'March' },
                { value: 6, label: 'June' },
                { value: 9, label: 'September' },
                { value: 12, label: 'December' }
            ];

            label = 'For the quarter ending';
        } else {
            options = [
                { value: '', label: '' },
                { value: 1, label: 'January' },
                { value: 2, label: 'February' },
                { value: 3, label: 'March' },
                { value: 4, label: 'April' },
                { value: 5, label: 'May' },
                { value: 6, label: 'June' },
                { value: 7, label: 'July' },
                { value: 8, label: 'August' },
                { value: 9, label: 'September' },
                { value: 10, label: 'October' },
                { value: 11, label: 'November' },
                { value: 12, label: 'December' }
            ];

            label = 'For the month of';
        }

        if (this.state.grossReceiptReturn.returnYear === new Date().getFullYear()) {
            const currentMonth = new Date().getMonth() + 1;
            options.forEach(option => {
                if (option.value > currentMonth) {
                    option.disabled = true;
                }
            });       
        }     

        return (
            <InputWithLabel
                label={label}
                type='select'
                id='returnMonth'
                value={this.state.grossReceiptReturn.returnMonth ?? ''}
                options={options}
                onChange={this.handleReturnMonth}
            />
        );
    }

    updateFiledBy = (event: ChangeEvent<HTMLInputElement>) => {
        const newValue = event.target.value;
        this.setState((oldState) => {
            return {
                ...oldState,
                grossReceiptReturn: {
                    ...oldState.grossReceiptReturn,
                    filedBy: newValue,
                },
            };
        });
    }

    buildReturnYearOptions = () => {
        const currentYear = new Date().getFullYear();

        return [
            { value: '', label: '' },
            { value: currentYear, label: currentYear.toString() },
            { value: (currentYear - 1), label: (currentYear - 1).toString() },
            { value: (currentYear - 2), label: (currentYear - 2).toString() },
            { value: (currentYear - 3), label: (currentYear - 3).toString() },
        ];
    }

    get returnInformation() {
        let columnList: React.ReactNode[] = [];
        columnList.push(
            <InputWithLabel
                label="Filed By"
                type="text"
                id="filedBy"
                fullWidth
                value={this.state.grossReceiptReturn.filedBy}
                onChange={this.updateFiledBy}
            />,
            this.buildPeriodDropDown(),
            <InputWithLabel
                label='Return Year'
                type='select'
                id='returnYear'
                value={this.state.grossReceiptReturn.returnYear ?? ''}
                onChange={this.handleReturnYear}
                options={this.buildReturnYearOptions()}
            />
        )
        columnList.push(
            <InputWithLabel
                label="Bill Due Date"
                type="date"
                id="dueDate"
                value={this.state.grossReceiptReturn.dueDate ?? ''}
                onChange={e => this.handleDateUpdate(e)}
                disabled
            />,
            <InputWithLabel
                label="Excess Tax $"
                type="text"
                id="excessTax"
                value={this.state.grossReceiptReturn.excess}
                onChange={e => this.updateCurrency(e, 8, "excess")}
                onBlur={e => this.updateCurrency(e, 8, "excess", true)}
            />
        );
        columnList.push(
            <InputWithLabel
                label="Credit Refunded $"
                type="text"
                id="creditRefunded"
                value={this.state.grossReceiptReturn.creditRefunded}
                onChange={e => this.updateCurrency(e, 8, "creditRefunded")}
                onBlur={e => this.updateCurrency(e, 8, "creditRefunded", true)}
            />,
            <InputWithLabel
                label="Credit To Be Applied $"
                type="text"
                id="creditApplied"
                value={this.state.grossReceiptReturn.creditApplied}
                onChange={e => this.updateCurrency(e, 8, "creditApplied")}
                onBlur={e => this.updateCurrency(e, 8, "creditApplied", true)}
            />,

        );

        return (
            <TaxpayerPortalPanel header="Return Information" className="bordered-panel borderless-bottom borderless-top return-info-container">
                {this.getFormattedColumns(columnList)}
                {this.info}
            </TaxpayerPortalPanel>
        );
    }

    get locations() {
        return (
            <TaxpayerPortalPanel header="Locations" className="bordered-panel borderless-top" >
                <table width="100%">
                    <tbody>
                        {(this.state.grossReceiptReturn.locations as (FoodBeverageReturnLocation | ReturnLocation)[]).map((loc, i) => {
                            return (
                                <tr key={i}>
                                    <td>
                                        <LocationInput
                                            location={loc}
                                            type={this.state.grossReceiptReturn.grossReceiptTypeCode}
                                            index={i}
                                            splitVehicleUnder={this.getSplitUnderValue(this.state.grossReceiptReturn.returnFiscalYear)}
                                            splitVehicleOver={this.getSplitOverValue(this.state.grossReceiptReturn.returnFiscalYear)}
                                            updateGrossReceipts={this.updateGrossReceiptsForLocation}
                                            updateNonPreparedReceipts={this.updateNonPreparedReceiptsForLocation}
                                            updateSplitVehicleReceipts={this.updateSplitVehicleReceiptsForLocation}
                                            updateRoomOccupancyReceipts={this.updateRoomOccupancyReceiptsForLocation}
                                        />
                                    </td>
                                </tr>
                            )
                        })}
                    </tbody>
                </table>
            </TaxpayerPortalPanel>
        );
    }

    render() {
        return (
            <div>
                {this.error}
                {this.floatingInfo}
                {
                    this.state.grossReceiptReturn.accountNum === '' ?
                        (
                            <Redirect to='/' />
                        ) :
                        `Create New Return`
                }
                {this.accountInformation}
                {this.returnInformation}
                {this.locations}
                <div className="center-flex">
                    <LoadingButtonBase label="Go To Final Review" className="submit-button payment-enabled" onClick={this.submitReturn} loading={this.state.loading}></LoadingButtonBase>
                </div>
            </div>
        );
    }
}
